import { Button, FormField, Modal, Select, useToast } from "@design";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  addSpaceConnectorMutation,
  type ConnectorSetting,
  errorMessage,
  getConnectorDetailOptions,
  listConnectorsOptions,
  listSpaceConnectorsQueryKey,
} from "@/api";
import { Text } from "@/components/Text";
import { connectorName } from "@/features/build/connectors/connectorName";
import { SettingValues } from "./SettingValues";

/** Pick one of my connectors (not already in the space) and set its values for this space. */
export function AddSpaceConnectorModal({
  spaceUuid,
  existing,
  onClose,
}: Readonly<{ spaceUuid: string; existing: string[]; onClose: () => void }>) {
  const { t } = useTranslation("spaces", { keyPrefix: "settings.connectors" });
  const { t: tc } = useTranslation("connectors");
  const toast = useToast();
  const queryClient = useQueryClient();
  const pickId = useId();
  const [picked, setPicked] = useState<string>("");
  const [values, setValues] = useState<Record<string, string>>({});
  const mine = useQuery({
    ...listConnectorsOptions({ query: { page_size: 100, include_org: true } }),
    select: (r) => r.data,
  });
  const detail = useQuery({
    ...getConnectorDetailOptions({ path: { connector_uuid: picked } }),
    enabled: Boolean(picked),
    select: (r) => r.data,
  });
  const add = useMutation(addSpaceConnectorMutation());
  const pending = add.isPending;
  // Modal re-runs its focus effect when onClose changes; keep it stable so typing stays in the field.
  const close = useCallback(() => !pending && onClose(), [pending, onClose]);
  const choices = (mine.data ?? []).filter((c) => !existing.includes(c.uuid) && !c.disabledByAdmin);
  const settings = ((detail.data?.endpoint as { settings?: ConnectorSetting[] } | undefined)?.settings ??
    []) as ConnectorSetting[];

  const submit = () =>
    add.mutate(
      { path: { space_uuid: spaceUuid }, body: { connectorUuid: picked, config: values } },
      {
        onSuccess: async (res) => {
          await queryClient.invalidateQueries({
            queryKey: listSpaceConnectorsQueryKey({ path: { space_uuid: spaceUuid } }),
          });
          toast.success(t("added", { name: connectorName(res.data.connector, tc) }));
          onClose();
        },
        onError: (error) => toast.error(errorMessage(error, t("failed"))),
      },
    );

  return (
    <Modal
      open
      onClose={close}
      title={t("addTitle")}
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={pending}>
            {t("cancel")}
          </Button>
          <Button variant="primary" loading={pending} disabled={!picked} onClick={submit}>
            {t("addAction")}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {mine.isSuccess && choices.length === 0 ? (
          <Text variant="body-sm" tone="secondary">
            {t("noneToAdd")}
          </Text>
        ) : (
          <FormField label={t("pick")} htmlFor={pickId}>
            <Select
              id={pickId}
              aria-label={t("pick")}
              placeholder={t("pickPlaceholder")}
              value={picked || undefined}
              options={choices.map((c) => ({ value: c.uuid, label: connectorName(c, tc) }))}
              onChange={(value) => {
                setPicked(value);
                setValues({});
              }}
            />
          </FormField>
        )}
        <SettingValues
          settings={settings}
          values={values}
          onChange={(name, value) => setValues((v) => ({ ...v, [name]: value }))}
        />
      </div>
    </Modal>
  );
}
